import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { AfterContentChecked, ChangeDetectorRef, Component,
	HostBinding, NgZone, OnDestroy, OnInit } from '@angular/core';
import moment from 'moment';
import { catchError, concatMap, forkJoin, groupBy, map,
	mergeMap, of, Subscription, switchMap, tap, toArray, zip } from 'rxjs';
import { RouterLink } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

import { User } from '../../shared/models/user';
import { TranslationService } from '../../shared/services/translation.service';
import { UserService } from '../../shared/services/user.service';
import { MillOffer, MillOfferResponse } from '../models/mill-offer';
import { MillOfferService } from '../services/mill-offer.service';
import { Need4SteelWizardService } from '../services/need4steel-wizard.service';
import { Need4SteelService } from '../services/need4steel.api.service';
import { UsageAnalytics } from '../../shared/services/usage-analytics.service';
import { BocClient } from '../../shared/models/boc-client-transac';
import { UsageEvent } from '../../shared/models/usage-event';
import { ErrorService } from '../../shared/services/error.service';
import { LoadingSpinnerComponent } from '../../shared/components/utils/loading-spinner.component';
import { DownloadImageService } from '../../shared/services/download-image.service';

@Component({
	selector: 'copo-need-steel-content',
	styleUrl: './need-steel-content.component.css',
	templateUrl: './need-steel-content.component.html',
	imports: [
		NgIf,
		TranslateModule,
		NgFor,
		MatRippleModule,
		RouterLink,
		LoadingSpinnerComponent,
		MatTooltipModule
	]
})
export class NeedSteelContentComponent implements OnInit, OnDestroy, AfterContentChecked {
	@HostBinding('class.passthrough') readonly passthrough = true;

	user: User;
	need4steelResponseSubscription?: Subscription;
	requiredFields: (keyof MillOffer)[] = ['id'];
	filters = '';
	millOfferList!: MillOfferResponse[];
	isLoading = true;
	errorDescription: string | undefined;
	weekYearList: string[] = [];
	families = ['HRBL6', 'HRPI6', 'CRCR6', 'ELZVI6', 'EGZVI6', 'HDZ6', 'HDZM6', 'OCOZ6', 'MSMS6'];
	distinctFamilies = new Set<string>();
	noWeightOption: boolean;
	errorSubscription: Subscription;
	sub: Subscription | undefined;
	groupDescriptions = new Map<string, string>();
	imagesUrlMap = new Map<string, string>();
	imageNames = [
		'thumbnail_BL',
		'thumbnail_CR',
		'thumbnail_PI',
		'thumbnail_ZM',
		'thumbnail_ZVI',
		'thumbnail_Z',
		'thumbnail_MS'
	];

	constructor(
		private readonly userService: UserService,
		private readonly need4SteelService: Need4SteelService,
		private readonly need4steelWizardService: Need4SteelWizardService,
		public usageAnalytics: UsageAnalytics,
		public millOfferService: MillOfferService,
		public errorService: ErrorService,
		readonly ngZone: NgZone,
		private readonly translationService: TranslationService,
		readonly changeDetectorRef: ChangeDetectorRef,
		private readonly downloadImageService: DownloadImageService,

	) {
		this.user = this.userService.user;
		this.need4steelWizardService.getState().stepIndex = 0;
		this.errorService.resetErrorMsg();
		this.noWeightOption = this.millOfferService.noWeightOption;
		this.errorSubscription = this.millOfferService.offerListErrMsgObs.subscribe(errMsg => {
			this.errorDescription = errMsg;
		});
	}

	ngOnInit() {
		if (!this.user.readOnly) {
			this.ngZone.runOutsideAngular(() => {
				this.sendN4SData();
			});
		}
		this.millOfferService.resetMillOfferDetails();
		this.need4steelResponseSubscription = forkJoin([
			forkJoin(this.imageNames.map(async key => this.fetchImage(key))),
			this.need4SteelService.getMillOffers(
				this.requiredFields,
				this.filters,
				0,
				60_000,
				false
			),
		]).pipe(
			map(([images, data]) => {
				for (const [i, imgResponse] of images.entries()) {
					const key = this.imageNames[i];
					if (imgResponse instanceof HttpErrorResponse) {
						this.imagesUrlMap.set(key, '');
					} else {
						this.imagesUrlMap.set(key, imgResponse);
					}
				}
				return data;
			}),
			concatMap(data => {
				for (const e of data.results) {
					const group = !e.metalStud ? `${ e.category }${ e.subCategory }${ e.form }` : `MSMS${ e.form }`;
					this.distinctFamilies.add(group);
					if (e.metalStud) {
						this.groupDescriptions.set(
							group,
							this.translationService.getTR('global-subcategory.MS')
						);
					} else {
						this.groupDescriptions.set(
							group,
							`${ e.subCategoryDescription }
						 ${ e.formDescription }`
						);
					}
				}
				return data.results;
			}),
			groupBy((millOffer: MillOffer) => `${ millOffer.deliveryWeek < 10
				? '0'
				: '' }${ millOffer.deliveryWeek }-${ millOffer.deliveryYear }`),
			mergeMap(group => zip(of(group.key), group.pipe(
				map(millOffer =>
					({ group: !millOffer.metalStud
						? `${ millOffer.category }${ millOffer.subCategory }${ millOffer.form }`
						: 'MSMS6',
					millOffer })),
				toArray()
			)).pipe(map(([key, millOffers]) => ({ key, millOffers })))),
			toArray()
		).subscribe({
			next: response => {
				this.millOfferList = response;
				if (this.millOfferList.length > 0) {
					this.createWeekList();
				}
				this.isLoading = false;
			},
			error: (err: unknown) => {
				console.error('unable to retrieve mill offers', err);
				if (!(err instanceof HttpErrorResponse)) {
					return;
				}
				this.errorDescription = err.status === <number> HttpStatusCode.NotFound
					? this.translationService.getTR('need4steel.soldto.shipto.error.message')
					: this.translationService.getTR('need4steel.offer.error.message');
				this.isLoading = false;
			}
		});
	}

	ngAfterContentChecked(): void {
		this.changeDetectorRef.detectChanges();
	}

	createWeekList() {
		const lowestWeekYear = this.millOfferList.reduce((a, b) => {
			const weekYearCurrent = a.key.split('-');
			const weekYearPrevious = b.key.split('-');
			return (weekYearCurrent[1] + weekYearCurrent[0])
				> (weekYearPrevious[1] + weekYearPrevious[0]) ? b : a;
		}).key;
		const highestWeekYear = this.millOfferList.reduce((a, b) => {
			const weekYearCurrent = a.key.split('-');
			const weekYearPrevious = b.key.split('-');
			return (weekYearCurrent[1] + weekYearCurrent[0])
				< (weekYearPrevious[1] + weekYearPrevious[0]) ? b : a;
		}).key;

		const [lowestWeekNumber, lowestYearNumber] = lowestWeekYear.split('-').map(Number);
		const [highestWeekNumber, highestYearNumber] = highestWeekYear.split('-').map(Number);
		for (let i = lowestYearNumber; i <= highestYearNumber; i++) {
			const numberOfWeeks = i === highestYearNumber
				? highestWeekNumber : moment().year(lowestYearNumber).isoWeeksInYear();
			const startWeekNumber = i > lowestYearNumber ? 1 : lowestWeekNumber;

			for (let j = startWeekNumber; j <= numberOfWeeks; j++) {
				this.weekYearList.push(`${ j < 10 ? '0' : '' }${ j }-${ i }`);
			}
		}
	}

	getMillOffersByWeek(weekYear: string) {
		const millOfferByWeek = this.millOfferList.find(e => e.key === weekYear);
		const millOffers: ({ group: string; millOffer: MillOffer } | undefined)[][] = [];
		if (millOfferByWeek) {
			for (const a of this.families) {
				if (this.distinctFamilies.has(a)) {
					const millOffersByGroup = millOfferByWeek.millOffers.filter(o => o.group === a);
					millOffers.push(millOffersByGroup);
				}
			}
		}
		return millOffers;
	}

	isFamilyExists(family: string) {
		return this.distinctFamilies.has(family);
	}

	getFamilyDescription(family: string) {
		return this.groupDescriptions.get(family);
	}

	async fetchImage(key: string) {
		return this.downloadImageService.downloadImage(key);
	}

	getIddDescription() {
		return this.millOfferList.map(a => a.millOffers[0].millOffer.iddDescription)[0];
	}

	getImageUrl(imagekey: string) {
		return this.imagesUrlMap.get(`thumbnail_${ imagekey.slice(2, -1) }`);
	}

	getImageTitle(imageKey: string) {
		switch (imageKey.slice(2, -1)) {
		case 'BL':
			return `${ this.translationService.getTR('global-subcategory.BL') } 
				- © Han maomin / Shutterstock.com`;

		case 'PI':
			return `${ this.translationService.getTR('global-subcategory.PI') } - © Bernard Mikolajczak`;

		case 'CR':
			return `${ this.translationService.getTR('global-subcategory.CR') } - © Jeroen Op de Beeck`;

		case 'ZM':
			return 'Magnelis®- © MAXSHOT.PL / Shutterstock.com';

		case 'ZVI':
			return 'Jetskin® - © Philippe Vandenameele';

		case 'Z':
			return `${ this.translationService.getTR('global-subcategory.Z') } - © David Dalla Costa / 
				Shutterstock.com`;

		case 'MS':
			return `${ this.translationService.getTR('global-subcategory.MS') } 
				- © Roman023_photography / Shutterstock.com`;

		default:
			return '';
		}
	}

	ngOnDestroy() {
		this.errorSubscription.unsubscribe();
		this.millOfferService.setOfferListErrorMsg('');
	}

	private sendN4SData() {
		this.sub = this.millOfferService.getBocs()
			.pipe(
				switchMap(userBocData => {
					if (userBocData.length === 0) {
						return of(<const>[userBocData, <BocClient[]>[]]);
					}
					const businessIds = Array.from(
						new Set(userBocData.flatMap(v => [v.soldTo]))
					);
					return forkJoin([
						of(userBocData),
						this.userService.findBocs(
							[
								'businessId',
								'name',
								'city',
								'address',
								'country',
								'postalCode',
								'referenceCommercialUnit',
								'referenceCommercialSubUnit',
								'referenceCommercialSubSubUnit'
							],
							businessIds.map(x => `businessId:${ x }`).join(' OR ')
						).pipe(
							map(v => v.results),
							catchError((err: unknown) => {
								console.error('unable to load boc data', err);
								return of([]);
							})
						),
					]);
				}),
			)
			.pipe(
				tap(async ([userBocData, bocs]) => {
					if (userBocData.length > 0) {
						this.sub = undefined;
						for (const boc of bocs) {
							const usageEvent: UsageEvent = {
								code: 'OFFERLIST',
								soldto: String(boc.businessId),
								codeUnitRef: boc.referenceCommercialUnit?.codeUnit,
								codeSubUnitRef: boc.referenceCommercialSubUnit?.codeSubUnit,
								codeSubSubUnitRef: boc.referenceCommercialSubSubUnit?.codeSubSubUnit,
							};
							await this.usageAnalytics.sendUsageAnalyticsEvent(usageEvent);
						}
					}
				}),
			).subscribe({
				error: (err: unknown) => {
					console.error('unable to send data', err);
					this.sub = undefined;
				},
			});
	}
}
